import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import Header from './Header';
import CatFollower from './CatFollower';
import { Link } from 'react-router-dom';
// ---- helpers ----
const PUB = (process.env.PUBLIC_URL ?? '').replace(/\/$/, '');
const resolvePublic = (p: string) =>
  /^https?:\/\//.test(p) ? p : `${PUB}${p.startsWith('/') ? '' : '/'}${p}`;

// ---- types ----
interface Project {
  id: string;
  title: string;
  blurb: string;
  images: string[]; // put your screenshots in /public/projects/<id>/...
  link?: string;    // add your repo/demo link here
  tags?: string[];
}

export default function Projects() {
  // 🔧 Edit the screenshots in `images` and add your links when ready.
  const projects: Project[] = [
    {
      id: 'Markhint',
      title: 'Markhint',
      blurb: 'A Website which has helped 100K+ students with their exams',
      images: [resolvePublic('/projects/markhint/markhint.webp')],
      link: 'https://markhint.in',
      tags: ['FULL STACK', 'PRODUCTION'],
    },
    {
      id: 'Color Picker React',
      title: 'Color Picker React',
      blurb: 'Color playground: palettes, gradients, and UI experiments.',
      images: [resolvePublic('/projects/colors_big/colors_big.webp')],
      link: 'https://github.com/fabsway23/colors_big',
      tags: ['REACT', 'MODULAR'],
    },
    {
      id: 'Covid-tracker',
      title: 'Covid-tracker',
      blurb: 'COVID dashboard / visualizer.',
      images: [resolvePublic('/projects/covid/covid.webp')],
      link: 'https://github.com/fabsway23/covid',
      tags: ['REACT', 'API', "DATA VIZ"],
    },

    {
      id: 'Photopia',
      title: 'Photopia',
      blurb: 'Photo Gallery (API BASED)',
      images: [resolvePublic('/projects/photopia/photopia.webp')],
      link: 'https://fabsway23.github.io/photopia',
      tags: ['JavaScript', 'API'],
    },
    {
      id: 'Website with fancy effects',
      title: 'Website with fancy effects',
      blurb: 'Website designed to practice fancy animations',
      images: [resolvePublic('/projects/proper-website/proper-website.webp')],
      link: 'https://fabsway23.github.io/proper-website',
      tags: ['JAVASCRIPT', 'GSAP', 'ANIMATION'],
    },
    {
      id: 'Color picker JS',
      title: 'Color picker JS',
      blurb: 'Classic color utilities and palette generator.',
      images: [resolvePublic('/projects/colors/colors.webp')],
      link: 'https://fabsway23.github.io/colors',
      tags: ['JavaScript', 'UI'],
    },
    {
      id: 'Beat_Maker',
      title: 'Beat Maker',
      blurb: 'Browser-based beat maker / step sequencer.',
      images: [resolvePublic('/projects/beat_maker/beat_maker.webp')],
      link: 'https://fabsway23.github.io/beat_maker',
      tags: ['JavaScript', 'Audio'],
    },
    {
      id: 'Todo_app_js',
      title: 'Todo app JS',
      blurb: 'Simple todo app with local state.',
      images: [resolvePublic('/projects/todo_app_js/todo_app_js.webp')],
      link: 'https://fabsway23.github.io/Todo_app_JS',
      tags: ['JavaScript', 'App'],
    },
  ];


  // ---- modal state ----
  const [active, setActive] = useState<Project | null>(null);
  const [imgIndex, setImgIndex] = useState(0);
  const backdropRef = useRef<HTMLDivElement | null>(null);

  const close = () => { setActive(null); setImgIndex(0); };

  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, imgIndex]);

  const openProject = (p: Project) => { setActive(p); setImgIndex(0); };
  const nextImage = () => active && setImgIndex(i => (i + 1) % active.images.length);
  const prevImage = () => active && setImgIndex(i => (i - 1 + active.images.length) % active.images.length);

  const onBackdropClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (e.target === backdropRef.current) close();
  };

  return (
    <Main>
      <Background />
      <Header />
      <Button as={Link} to={"/"}>{`<- Home Page`}</Button>
      <ContentWrap>
        <TitleRow>
          <H1>Projects</H1>
          <Hint>Click a tile to preview</Hint>
        </TitleRow>

        <Grid>
          {projects.map((p) => (
            <Tile key={p.id} onClick={() => openProject(p)}>
              <ThumbWrap>
                <Thumb
                  src={p.images[0] ?? resolvePublic(`projects/${p.title}/thumb.png`)}
                  alt={p.title}
                  onError={(e) => { (e.currentTarget as HTMLImageElement).src = resolvePublic('/projects/placeholder.png'); }}
                />
              </ThumbWrap>
              <TileBody>
                <TileTitle>{p.title}</TileTitle>
                <TileBlurb>{p.blurb}</TileBlurb>
                {p.tags && p.tags.length > 0 && (
                  <Tags>{p.tags.map((t) => <Tag key={t}>{t}</Tag>)}</Tags>
                )}
              </TileBody>
            </Tile>
          ))}
        </Grid>
      </ContentWrap>

      {active && (
        <Backdrop ref={backdropRef} onMouseDown={onBackdropClick}>
          <Modal role="dialog" aria-modal="true" aria-label={`${active.title} preview`} onMouseDown={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{active.title}</ModalTitle>
              <CloseBtn onClick={close} aria-label="Close">✕</CloseBtn>
            </ModalHeader>
            <ModalBody>
              <PreviewArea>
                {active.images.length > 1 && <NavBtnLeft onClick={prevImage} aria-label="Previous">‹</NavBtnLeft>}
                <PreviewImg
                  key={`${active.id}-${imgIndex}`}
                  src={active.images[imgIndex]}
                  alt={`${active.title} screenshot ${imgIndex + 1}`}
                  onError={(e) => { (e.currentTarget as HTMLImageElement).src = resolvePublic('/projects/placeholder.png'); }}
                />
                {active.images.length > 1 && <NavBtnRight onClick={nextImage} aria-label="Next">›</NavBtnRight>}
              </PreviewArea>
              <Desc>
                <DescText>{active.blurb}</DescText>
                {active.link && <ExtLink href={active.link} target="_blank" rel="noreferrer">Open link ↗</ExtLink>}
              </Desc>
            </ModalBody>
          </Modal>
        </Backdrop>
      )}

      <Noise />
      <CRTOverlay />
  
    </Main>
  );
}

/* ---------- styled ---------- */

const CRTOverlay = styled.div`
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 2147483646;
  background: repeating-linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0.01) 0px,
    rgba(0, 0, 0, 0) 1px,
    transparent 2px
  );
  mix-blend-mode: overlay;
  opacity: 0.4;
`;

const Main = styled.div`
  min-height: 100vh; /* allow page to grow */
  position: relative;
  
`;

const ContentWrap = styled.div`
  position: relative;
  z-index: 10;
  max-width: 1200px;
  margin: 88px auto 64px;
  padding: 0 20px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 16px;
  margin-bottom: 20px;
`;

const H1 = styled.h1`
  margin: 0;
  color: #e4ce01;
  font-size: 3rem;
  line-height: 1;
`;

const Hint = styled.span`
  color: #54b990;
  opacity: 0.9;
  font-size: 0.95rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr)); /* wider tiles */
  gap: 24px;
`;

const Tile = styled.button`
  all: unset;
  display: grid;
  grid-template-rows: auto 1fr;
  background: rgba(0, 0, 0, 0.35);
  border: 1.5px solid #00779d;
  border-right-width: 3px;
  border-bottom-width: 3px;
  border-radius: 10px;
  cursor: pointer;
  overflow: hidden;
  transition: transform 120ms ease, box-shadow 120ms ease, filter 120ms ease;
  backdrop-filter: blur(2px);
  -webkit-tap-highlight-color: transparent;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 18px rgba(0, 0, 0, 0.35);
    filter: brightness(1.03);
  }
  &:focus-visible {
    outline: 2px dashed #e4ce01;
    outline-offset: 3px;
  }
`;

const ThumbWrap = styled.div`
  position: relative;
  background: #001318;
  aspect-ratio: 16 / 10;
  overflow: hidden;
`;

const Thumb = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  image-rendering: -webkit-optimize-contrast;
  display: block;
`;

const TileBody = styled.div`
  padding: 12px 12px 14px;
  color: #c8f6e3;
`;

const TileTitle = styled.h3`
  margin: 0 0 6px;
  color: #e4ce01;
  font-size: 1.1rem;
`;

const TileBlurb = styled.p`
  margin: 0;
  color: #54b990;
  font-size: 0.95rem;
  line-height: 1.35;
`;

const Tags = styled.div`
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Tag = styled.span`
  font-size: 0.8rem;
  color: #9ae6c9;
  border: 1px solid #2baf8b;
  padding: 2px 8px;
  border-radius: 999px;
`;

/* Backdrop above CRT + Noise */
const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 12, 16, 0.6);
  backdrop-filter: blur(2px);
  z-index: 2147483649;
  display: grid;
  place-items: center;
  padding: 20px;
`;

const Modal = styled.div`
  width: min(980px, 92vw);
  max-height: min(86vh, 980px);
  background: #02161c;
  border: 2px solid #00779d;
  border-right-width: 4px;
  border-bottom-width: 4px;
  border-radius: 14px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.6);
  display: grid;
  grid-template-rows: auto 1fr;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: space-between;
  padding: 12px 14px;
  background: linear-gradient(180deg, rgba(0, 46, 60, 0.8), rgba(0, 24, 31, 0.7));
  border-bottom: 1px solid #004c61;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: #e4ce01;
  font-size: 1.2rem;
`;

const CloseBtn = styled.button`
  all: unset;
  cursor: pointer;
  color: #c8f6e3;
  font-size: 1.1rem;
  line-height: 1;
  padding: 6px 8px;
  border: 1px solid transparent;
  border-radius: 8px;

  &:hover {
    color: #ffb2b2;
    background: rgba(255, 0, 0, 0.08);
    border-color: rgba(255, 0, 0, 0.2);
  }
  &:focus-visible {
    outline: 2px dashed #e4ce01;
    outline-offset: 3px;
  }
`;

const ModalBody = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 14px;
  padding: 14px;
  min-height: 360px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto;
  }
`;

const PreviewArea = styled.div`
  position: relative;
  border: 1px solid #004c61;
  border-radius: 10px;
  background: #001318;
  min-height: 280px;
  overflow: hidden;
`;

const PreviewImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  image-rendering: -webkit-optimize-contrast;
  user-select: none;
  pointer-events: none;
`;

const navBtnBase = `
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  border: 1px solid #00779d;
  background: rgba(0, 46, 60, 0.6);
  color: #e4ce01;
  padding: 8px 10px;
  border-radius: 10px;
  cursor: pointer;
  user-select: none;
  line-height: 1;
  font-size: 1.2rem;
  transition: filter 120ms ease, transform 120ms ease, background 120ms ease;

  &:hover { filter: brightness(1.1); transform: translateY(-50%) scale(1.03); }
  &:active { transform: translateY(-50%) scale(0.98); }
`;

const NavBtnLeft = styled.button`${navBtnBase} left: 10px;`;
const NavBtnRight = styled.button`${navBtnBase} right: 10px;`;

const Desc = styled.div`
  border: 1px solid #004c61;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.35);
  padding: 14px;
  color: #c8f6e3;
  display: grid;
  align-content: start;
  gap: 10px;
`;

const DescText = styled.p`
  margin: 0;
  color: #9ae6c9;
  line-height: 1.45;
`;

const ExtLink = styled.a`
  color: #e4ce01;
  text-decoration: none;
  width: fit-content;
  border-bottom: 1px dashed rgba(228, 206, 1, 0.6);
  &:hover { filter: brightness(1.1); }
`;

const Noise = styled.div`
  position: fixed; inset: 0; pointer-events: none;
  z-index: 2147483647;
  background-image: url(${resolvePublic('/noise.png')});
  background-repeat: repeat;
  background-size: 256px 256px;
  opacity: 0.02;
`;

/* ✅ fixed full-page background so it never goes white on scroll */
const Background = styled.div`
  position: fixed;
  inset: 0;
  background-image: url(${resolvePublic('/star_bg.webp')});
  background-size: cover;
  background-repeat: repeat;
  z-index: -1;
  opacity: 1;
`;
const Button = styled.div`
    display: block;
  font-family: 'ChiKareGo2', monospace;
  font-size: 2rem;
  color: #fffd46; /* yellow text */
  background-color: #004f8a; /* dark blue fill */
  border: 2px solid #00c4ff; /* bright blue border */
  padding: 2px 6px; /* tight and square */
  width: fit-content;
  clip-path: polygon(
    0 3px,
    3px 0,
    calc(100% - 3px) 0,
    100% 3px,
    100% calc(100% - 3px),
    calc(100% - 3px) 100%,
    3px 100%,
    0 calc(100% - 3px)
  );
  image-rendering: pixelated;
  line-height: 1;
  margin-top: 8px; /* optional spacing from icon above */
  position:absolute;
  top:8vh;
  left:5vw;

  &:hover{
  background: #325e81ff;
  color: #fffd46; 

  }
  
`;